import type { ReactNode } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";

type RouterOutputs = inferRouterOutputs<AppRouter>;
export type LayoutTemplateUsageMetrics =
  RouterOutputs["admin"]["brands"]["socialPostLayoutTemplateUsage"];
export type LayoutTemplateUsageMetric = LayoutTemplateUsageMetrics["entries"][number];

export const formatLayoutTemplateSelectionRate = (rate: number): string =>
  `${Math.round(rate * 100)}%`;

export const getLayoutTemplateUsageMetric = (
  metrics: LayoutTemplateUsageMetrics | undefined,
  templateId: string,
): LayoutTemplateUsageMetric | null =>
  metrics?.entries.find((entry) => entry.templateId === templateId) ?? null;

const formatDateTime = (value: string): string => new Date(value).toLocaleString();

const formatLayoutTemplateSelectionCount = (count: number): string =>
  count === 1 ? "1 selection" : `${count} selections`;

export const TemplateStat = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="rounded-md border bg-muted/30 px-3 py-2">
    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
      {label}
    </div>
    <div className="mt-1 text-sm font-medium text-foreground">{value}</div>
  </div>
);

export const LayoutTemplateUsageSummary = ({
  metric,
  isLoading,
  isError,
}: {
  metric: LayoutTemplateUsageMetric | null;
  isLoading: boolean;
  isError: boolean;
}) => {
  if (isLoading) {
    return <span>Usage loading</span>;
  }

  if (isError) {
    return <span>Usage unavailable</span>;
  }

  const selectedCount = metric?.selectedCount ?? 0;
  const rate = metric?.selectionRate ?? 0;

  return (
    <span>
      {formatLayoutTemplateSelectionCount(selectedCount)} /{" "}
      {formatLayoutTemplateSelectionRate(rate)}
      {metric?.lastSelectedAt ? ` / last ${formatDateTime(metric.lastSelectedAt)}` : ""}
    </span>
  );
};
